import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import {
    findClosestStyleWithFallback,
    IAvailableFontWithStyles,
    IOptionalStyleForFont,
} from '../../../models/configs/fonts.model';
import { catchErrorTypedAsync } from '../../../services/functions-helper.service';
import { SnackbarActionEnum, SnackBarService } from '../../../services/utils/snack-bar.service';
import { Subject, takeUntil } from 'rxjs';
import { IExtraFontSettings, IFontIDWithFamily } from '../../../models/style-font-model';
import { AvailableFontManagerAbstractClass } from '../../major/style-settings/font/available-font-manager-abstract-class';
import { FontWeightStyle } from 'lottie-json-helper/lib/types';
import { MatExpansionPanel } from '@angular/material/expansion';
import { ConfigurationService } from '../../../services/configuration.service';
import { MatMenuTrigger } from '@angular/material/menu';

@Component({
    selector: 'app-font-selector',
    templateUrl: './font-selector.component.html',
    styleUrl: './font-selector.component.scss',
})
export class FontSelectorComponent implements OnInit, OnDestroy {
    @Input({ required: true, alias: 'font-service' })
    fontService: AvailableFontManagerAbstractClass;

    @Input({ required: true, alias: 'toDisplayWeightPick' })
    toDisplayWeightPick: boolean;

    @Input({ required: true, alias: 'iconMode' })
    iconMode: boolean;

    @ViewChild('fontMenuTrigger') fontMenuTrigger!: MatMenuTrigger;

    currentFont: IAvailableFontWithStyles;

    fontFromInput: IExtraFontSettings;

    loadingFontId: string | null = null;

    @Input({ required: true, alias: 'currentFont' })
    set currentFontSet(val: IExtraFontSettings) {
        this.fontFromInput = val;
        if (val) {
            this.setCurrentFontSettings(this.fontFromInput.style);
        }
    }

    @Output()
    onFontSelected = new EventEmitter<IFontIDWithFamily>();

    onDestroy$ = new Subject<boolean>();

    localFonts: IAvailableFontWithStyles[];

    baseCdnPath: string;

    constructor(
        private snackBarService: SnackBarService,
        private configService: ConfigurationService
    ) {
        this.baseCdnPath = this.configService.baseCdnUrl;
    }

    ngOnInit(): void {
        this.fontService.allFonts$.pipe(takeUntil(this.onDestroy$)).subscribe((fonts) => {
            this.localFonts = fonts;
            this.setCurrentFontSettings(this.fontFromInput?.style);
        });
    }

    async newFontSelected(font: IFontIDWithFamily, style: IOptionalStyleForFont) {
        this.loadingFontId = font.id;
        this.onFontSelected.emit(font);
        this.currentFont.style = style.id;
        const fakeFontToSendToUpdate = { ...font };

        const { data, error } = await catchErrorTypedAsync(
            this.fontService.newFontSelectedAsync(fakeFontToSendToUpdate, this.currentFont, style)
        );
        this.loadingFontId = null;
        if (error) {
            this.snackBarService.openMessage(
                'An error occurred while picking new font',
                SnackbarActionEnum.Close,
                5000
            );
            return;
        }

        // this.currentFont.family = font.family;
        // this.currentFont.id = font.id;
        this.closeFontMenu();
        this.setCurrentFontSettings(this.currentFont.style);
    }

    async newStyleSelected(newFont: IAvailableFontWithStyles, style?: IOptionalStyleForFont) {
        if (!newFont) {
            return;
        }
        if (!style && !this.currentFont?.style) {
            throw new Error('No style found');
        }
        if (!style) {
            const newStyle = this.getNewFontWeight(this.currentFont.style, newFont);
            const allFontStyles = newFont.optionalStyles.find((s) => s.id === newStyle);
            style = allFontStyles;
        }

        this.newFontSelected(newFont, style);
    }

    onFontHeaderClick(font: IAvailableFontWithStyles, event: MouseEvent): void {
        if (this.toDisplayWeightPick) {
            return;
        }
        event.stopPropagation();
        this.newStyleSelected(font, null);
    }

    onAttemptExpand(panel: MatExpansionPanel) {
        if (!this.toDisplayWeightPick) {
            panel.close();
        }
    }

    private setCurrentFontSettings(style: FontWeightStyle) {
        if (!this.fontFromInput || !this.localFonts) {
            return;
        }
        // Find the current font
        const foundFont = this.localFonts.find((f) => f.id.toLowerCase() === this.fontFromInput.id.toLowerCase());

        // Sort all fonts alphabetically by font name
        const sortedFonts = [...this.localFonts].sort((a, b) => a.name.localeCompare(b.name));

        if (!foundFont) {
            // The selected font isn't part of the available list (e.g. not existed in thd bb).
            // Still show the full list so the user can pick a new font - there's just no
            // active highlight. We keep a minimal currentFont built from the input so the
            // list renders and a replacement still carries the correct "original font".
            this.currentFont = this.buildFallbackCurrentFont();
            this.localFonts = sortedFonts;
            return;
        }

        // Deep clone to avoid shared reference
        this.currentFont = JSON.parse(JSON.stringify(foundFont));

        // Put currentFont at the beginning, excluding it from the sorted remainder
        this.localFonts = [
            this.currentFont,
            ...sortedFonts.filter((f) => f.id.toLowerCase() !== this.currentFont!.id.toLowerCase()),
        ];
        this.setCurrentStyle(style);
    }

    private buildFallbackCurrentFont(): IAvailableFontWithStyles {
        // fontFromInput (IExtraFontSettings) carries the original font's id/family/style,
        // which is exactly what we need for both rendering and the replacement payload.
        return {
            ...this.fontFromInput,
            whiteThumbSrc: '',
            blackThumbSrc: '',
            availableStyles: [],
            fallbacks: [],
            optionalStyles: [],
        };
    }

    private setCurrentStyle(style: FontWeightStyle) {
        const newStyle = this.getNewFontWeight(style, this.currentFont);
        this.currentFont.style = newStyle;
    }

    private getNewFontWeight(style: FontWeightStyle, font: IAvailableFontWithStyles): FontWeightStyle {
        const allFontStyles = font?.optionalStyles ? font.optionalStyles.map((s) => s.id) : [];
        const newStyleFromFallbacks = findClosestStyleWithFallback(style, allFontStyles);
        if (!newStyleFromFallbacks) {
            return null;
        }
        return newStyleFromFallbacks;
    }

    private closeFontMenu() {
        if (this.fontMenuTrigger) {
            this.fontMenuTrigger.closeMenu();
        }
    }

    ngOnDestroy(): void {
        this.onDestroy$.next(true);
        this.onDestroy$.complete();
    }
}
